/**
 * DQ-8820: the re-run entry points build their URL by string interpolation, so
 * an execution whose Execution Group / Execution Sub Group fields are absent
 * produces literal `null` / `undefined` query values. Those placeholders were
 * forwarded to the re-run endpoint as `NaN`, the request failed and the re-run
 * screen stayed empty (no Execution Sub Group, no prior run parameters).
 *
 * Values are therefore sanitised on both sides: omitted when the URL is built
 * and normalised to `null` when it is parsed.
 */

const PLACEHOLDER_VALUES = ["", "null", "undefined", "NaN"];

export interface IReRunExecutionParams {
  runId: string | null;
  runName: string | null;
  executionSubGroupId: string | null;
  executionSubGroupCode: string | null;
}

export interface IReRunValidateResourceUrlParams {
  domainId: number | string | null | undefined;
  resourceId: number | string | null | undefined;
  validationId: number | string | null | undefined;
  runId?: number | string | null;
  runName?: string | null;
  executionSubGroupId?: number | string | null;
  executionSubGroupCode?: string | null;
}

export interface IExecutionSubGroupParams {
  execution_sub_group_id?: number | string | null;
  execution_sub_group_code?: string | null;
}

/** Returns the trimmed value, or `null` for missing/placeholder values. */
export const sanitizeQueryParamValue = (value: unknown): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return PLACEHOLDER_VALUES.includes(trimmed) ? null : trimmed;
};

/** Coerces a query value to a number, or `null` when it is not numeric. */
export const toNullableNumber = (value: unknown): number | null => {
  const sanitized = sanitizeQueryParamValue(value);
  if (sanitized === null) return null;
  const parsed = Number(sanitized);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Reads the Execution Group / Sub Group re-run params off a location search. */
export const parseReRunExecutionParams = (
  search: string,
): IReRunExecutionParams => {
  const params = new URLSearchParams(search);
  return {
    runId: sanitizeQueryParamValue(params.get("run-id")),
    runName: sanitizeQueryParamValue(params.get("run-name")),
    executionSubGroupId: sanitizeQueryParamValue(
      params.get("execution-sub-group-id"),
    ),
    executionSubGroupCode: sanitizeQueryParamValue(
      params.get("execution-sub-group-code"),
    ),
  };
};

/**
 * Resolves the subgroup for a validation re-run. The route represents the
 * source execution selected by the user, so its subgroup must take precedence
 * over the response returned by the re-run data endpoint.
 */
export const resolveReRunExecutionSubGroup = (
  reRunParams: IReRunExecutionParams,
  responseSubGroup: IExecutionSubGroupParams | null | undefined,
): Required<IExecutionSubGroupParams> => ({
  execution_sub_group_id:
    toNullableNumber(reRunParams.executionSubGroupId) ??
    toNullableNumber(responseSubGroup?.execution_sub_group_id) ??
    0,
  execution_sub_group_code:
    sanitizeQueryParamValue(reRunParams.executionSubGroupCode) ??
    sanitizeQueryParamValue(responseSubGroup?.execution_sub_group_code) ??
    "",
});

/** Builds the re-run URL, omitting params the execution does not carry. */
export const buildReRunValidateResourceUrl = ({
  domainId,
  resourceId,
  validationId,
  runId,
  runName,
  executionSubGroupId,
  executionSubGroupCode,
}: IReRunValidateResourceUrlParams): string => {
  const params = new URLSearchParams();
  params.set("re-run", "true");

  const optionalParams: [string, unknown][] = [
    ["validation-id", validationId],
    ["run-id", runId],
    ["run-name", runName],
    ["execution-sub-group-id", executionSubGroupId],
    ["execution-sub-group-code", executionSubGroupCode],
  ];
  optionalParams.forEach(([key, value]) => {
    const sanitized = sanitizeQueryParamValue(value);
    if (sanitized !== null) params.set(key, sanitized);
  });

  return `/resource/${domainId}/re-run-validate-resource/${resourceId}?${params.toString()}`;
};
